// Pure derivation of stage notice toasts from room store state. The React
// driver (StageToasts) maps these specs onto sonner calls and controller
// commands; the controller never touches toast state directly. Stable ids let
// updates replace a toast in place, and `duration: Infinity` keeps state
// notices visible until resolved or dismissed.

export type NoticeToastTone = "info" | "warning" | "error";

export interface NoticeToastText {
  audioUnlockTitle: string;
  audioUnlockAction: string;
  cameraTitle: string;
  screenAudioTitle: string;
  screenAudioDescription: string;
  screenAudioDismiss: string;
  dismiss: string;
  updateTitle: string;
  updateReason: string;
  updateAction: string;
}

export interface NoticeToastState {
  audioUnlockVisible: boolean;
  cameraStatus: string;
  screenAudioWarningVisible: boolean;
  updateNoticeVisible: boolean;
  updateNoticeReason: "ready" | "sharing" | "watching";
  updateCanApply: boolean;
}

export interface NoticeToastSpec {
  id: string;
  tone: NoticeToastTone;
  title: string;
  description: string;
  actionLabel?: string;
  cancelLabel?: string;
  top: boolean;
}

export function describeNoticeToasts(
  state: NoticeToastState,
  text: NoticeToastText,
): NoticeToastSpec[] {
  const specs: NoticeToastSpec[] = [];
  if (state.audioUnlockVisible) {
    specs.push({
      id: "audio-unlock",
      tone: "info",
      title: text.audioUnlockTitle,
      description: "",
      actionLabel: text.audioUnlockAction,
      top: false,
    });
  }
  if (state.cameraStatus) {
    specs.push({
      id: "camera-status",
      tone: "error",
      title: text.cameraTitle,
      description: state.cameraStatus,
      cancelLabel: text.dismiss,
      top: false,
    });
  }
  if (state.screenAudioWarningVisible) {
    specs.push({
      id: "screen-audio-warning",
      tone: "warning",
      title: text.screenAudioTitle,
      description: text.screenAudioDescription,
      cancelLabel: text.screenAudioDismiss,
      top: false,
    });
  }
  if (state.updateNoticeVisible) {
    specs.push({
      id: "update-notice",
      tone: "info",
      title: text.updateTitle,
      description: text.updateReason,
      actionLabel: state.updateCanApply ? text.updateAction : undefined,
      cancelLabel: text.dismiss,
      top: true,
    });
  }
  return specs;
}
